// src/components/layout/Dashboard/Users/OrderHistory.jsx
import { useState, useEffect, useContext } from "react";
import { postData } from "../../../../services/Fetchnodeservices"; 
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext"; // 👈 ThemeContext Import

// Icons
const SpinnerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }}/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const OrderStatusBadge = ({ status, theme }) => {
  const statusMap = {
    delivered: { bg: `${theme.success}1a`, color: theme.success, text: "Delivered" },
    completed: { bg: `${theme.success}1a`, color: theme.success, text: "Completed" },
    pending: { bg: `${theme.warning}1a`, color: theme.warning, text: "Pending" },
    placed: { bg: `${theme.primary}1a`, color: theme.primary, text: "Placed" },
    accepted: { bg: `${theme.primary}1a`, color: theme.primary, text: "Accepted" },
    processing: { bg: `${theme.primary}1a`, color: theme.primary, text: "Processing" },
    cancelled: { bg: `${theme.error}1a`, color: theme.error, text: "Cancelled" },
    confirmed: { bg: `${theme.primary}1a`, color: theme.primary, text: "Confirmed" },
    shipped: { bg: `${theme.info}1a`, color: theme.info, text: "Shipped" }
  };
  const c = statusMap[status?.toLowerCase()] || { bg: `${theme.textMuted}1a`, color: theme.textMuted, text: status || "Unknown" };
  return (
    <span style={{ fontSize: "11px", fontWeight: 600, padding: "4px 10px", borderRadius: "12px", background: c.bg, color: c.color }}>
      {c.text}
    </span>
  );
};

const OrderHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = location.state?.user;
  const { theme } = useContext(ThemeContext); // 👈 Get dynamic theme
  
  console.log("Received user data in OrderHistory:", user);

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    } else {
      setLoading(false);
      setError("No user data received");
    }
  }, [user?.id]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await postData("users/ordersbyid", { userid: user?.id });
      console.log("Orders response:", response);
      
      let ordersData = [];
      if (response?.status === true && Array.isArray(response?.data)) {
        ordersData = response.data;
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        ordersData = response.data;
      }
      
      console.log("Extracted orders data:", ordersData);
      console.log("Total orders count:", ordersData.length);
      
      setAllOrders(ordersData);
      setOrders(ordersData);
      
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const filterOrders = (type) => {
    setActiveTab(type);
    
    if (type === "all") {
      setOrders(allOrders);
    } else if (type === "delivered") {
      setOrders(allOrders.filter(o => 
        o.order_status_user?.toLowerCase() === "delivered" || 
        o.order_status_driver?.toLowerCase() === "delivered"
      ));
    } else if (type === "pending") {
      setOrders(allOrders.filter(o => {
        const status = (o.order_status_user || o.order_status_restro || "").toLowerCase();
        return status === "pending" || status === "processing" || status === "confirmed" || status === "placed" || status === "accepted";
      }));
    } else if (type === "cancelled") {
      setOrders(allOrders.filter(o => 
        o.order_status_user?.toLowerCase() === "cancelled" || 
        o.order_status_restro?.toLowerCase() === "cancelled"
      ));
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getItemsList = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      if (Array.isArray(items)) {
        return items.map(item => `${item.name} x${item.qty}`).join(", ");
      }
      return itemsStr;
    } catch {
      return itemsStr || "-";
    }
  };

  // Calculate statistics
  const stats = {
    total: allOrders.length,
    delivered: allOrders.filter(o => 
      o.order_status_user?.toLowerCase() === "delivered" || 
      o.order_status_driver?.toLowerCase() === "delivered"
    ).length,
    pending: allOrders.filter(o => {
      const status = (o.order_status_user || o.order_status_restro || "").toLowerCase();
      return status === "pending" || status === "processing" || status === "confirmed" || status === "placed" || status === "accepted";
    }).length,
    cancelled: allOrders.filter(o => 
      o.order_status_user?.toLowerCase() === "cancelled" || 
      o.order_status_restro?.toLowerCase() === "cancelled"
    ).length,
    totalSpent: allOrders.reduce((sum, order) => {
      const amount = parseFloat(order.final_amount) || parseFloat(order.amount) || 0;
      return sum + amount;
    }, 0)
  };

  const ViewOrderModal = ({ order, onClose }) => {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }} onClick={onClose}>
        <div style={{
          background: theme.surface,
          borderRadius: "16px",
          maxWidth: "750px",
          width: "100%",
          maxHeight: "85vh",
          overflow: "auto",
          border: `1px solid ${theme.border}`
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            padding: "20px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            background: theme.surface,
            zIndex: 1
          }}>
            <h3 style={{ margin: 0, color: theme.textPrimary }}>Order Details #{order.id}</h3>
            <button onClick={onClose} style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: theme.textMuted
            }}>×</button>
          </div>
          <div style={{ padding: "20px" }}>
            {/* Order Info */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
              gap: "16px", 
              marginBottom: "20px",
              background: theme.surfaceAlt,
              padding: "16px",
              borderRadius: "12px"
            }}>
              <div>
                <strong style={{ color: theme.textPrimary }}>Order ID:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary, fontFamily: "monospace" }}>#{order.id}</p>
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>Date & Time:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary }}>{formatDateTime(order.created_at)}</p>
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>Payment Method:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary }}>{order.pay_method || "N/A"}</p>
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>Payment Status:</strong>
                <p style={{ 
                  margin: "4px 0 0", 
                  color: order.pay_status === "paid" ? theme.success : theme.warning,
                  fontWeight: 600
                }}>
                  {order.pay_status === "paid" ? "✓ Paid" : "Pending"}
                </p>
              </div>
            </div>

            {/* Items Details */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: theme.textPrimary }}>Items Ordered:</strong>
              <div style={{ 
                marginTop: "8px", 
                background: theme.surfaceAlt, 
                padding: "12px", 
                borderRadius: "8px",
                border: `1px solid ${theme.border}`
              }}>
                {getItemsList(order.items)}
              </div>
            </div>

            {/* Price Breakdown */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: theme.textPrimary }}>Price Breakdown:</strong>
              <div style={{ marginTop: "8px", background: theme.surfaceAlt, padding: "16px", borderRadius: "8px", border: `1px solid ${theme.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: theme.textSecondary }}>Item Total:</span>
                  <span style={{ color: theme.textPrimary }}>₹{order.price || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: theme.textSecondary }}>Delivery Charge:</span>
                  <span style={{ color: theme.textPrimary }}>₹{order.delivery_charge || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: theme.textSecondary }}>Tax:</span>
                  <span style={{ color: theme.textPrimary }}>₹{order.tax || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: theme.textSecondary }}>Platform Fee:</span>
                  <span style={{ color: theme.textPrimary }}>₹{order.user_platform_fee || order.business_platform_fee || 0}</span>
                </div>
                {order.discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span style={{ color: theme.textSecondary }}>Discount:</span>
                    <span style={{ color: theme.success }}>-₹{order.discount}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "12px", marginTop: "8px", borderTop: `1px solid ${theme.border}` }}>
                  <strong style={{ color: theme.textPrimary, fontSize: "16px" }}>Total Amount:</strong>
                  <strong style={{ color: theme.success, fontSize: "18px" }}>₹{order.final_amount || order.amount || 0}</strong>
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: theme.textPrimary }}>Delivery Information:</strong>
              <div style={{ marginTop: "8px", background: theme.surfaceAlt, padding: "16px", borderRadius: "8px" }}>
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ color: theme.textMuted, fontSize: "11px", marginBottom: "4px" }}>Delivery Address</div>
                  <div style={{ color: theme.textSecondary }}>{order.dropoff_address}</div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ color: theme.textMuted, fontSize: "11px", marginBottom: "4px" }}>Pickup Address</div>
                  <div style={{ color: theme.textSecondary }}>{order.pickup_address}</div>
                </div>
                {order.pickup_otp && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${theme.border}` }}>
                    <div style={{ color: theme.textMuted, fontSize: "11px", marginBottom: "4px" }}>Pickup OTP</div>
                    <div style={{ color: theme.textPrimary, fontFamily: "monospace", fontSize: "18px", fontWeight: 700 }}>{order.pickup_otp}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show error if no user data
  if (!user) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: theme.background, 
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}>
        <div style={{
          background: theme.surface,
          padding: "40px",
          borderRadius: "16px",
          textAlign: "center",
          border: `1px solid ${theme.border}`
        }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <h3 style={{ color: theme.textPrimary, marginBottom: "8px" }}>No User Data</h3>
          <p style={{ color: theme.textMuted }}>Please go back and select a user to view orders.</p>
          <button
            onClick={handleBack}
            style={{
              marginTop: "16px",
              padding: "8px 24px",
              background: theme.primary,
              border: "none",
              borderRadius: "8px",
              color: "#fff",
              cursor: "pointer"
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: theme.background, 
      fontFamily: "'DM Sans', sans-serif",
      padding: "20px 24px"
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .order-container {
          animation: slideIn 0.3s ease;
        }
      `}</style>

      <div className="order-container">
        {/* Header with Back Button */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "16px", 
          marginBottom: "24px",
          flexWrap: "wrap"
        }}>
          <button 
            onClick={handleBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "10px",
              cursor: "pointer",
              color: theme.textSecondary,
              fontSize: "13px",
              fontWeight: 500,
              transition: "all 0.2s ease"
            }}
          >
            <ChevronLeftIcon />
            Back to Users
          </button>
          
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: theme.textPrimary }}>
              Order History
            </h1>
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: theme.textMuted }}>
              {user?.name} • {user?.contact} • User ID: {user?.id}
            </p>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div style={{
            background: `${theme.error}1a`,
            border: `1px solid ${theme.error}40`,
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontSize: "13px", color: theme.error }}>⚠ {error}</span>
            <button 
              onClick={fetchOrders}
              style={{
                padding: "4px 12px",
                borderRadius: "6px",
                border: "none",
                background: theme.surface,
                color: theme.primary,
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div style={{ 
            display: "flex", 
            flexDirection: "column",
            alignItems: "center", 
            justifyContent: "center", 
            padding: "80px 20px",
            background: theme.surface,
            borderRadius: "16px",
            border: `1px solid ${theme.border}`
          }}>
            <SpinnerIcon />
            <p style={{ marginTop: "16px", color: theme.textMuted }}>Loading orders...</p>
          </div>
        ) : (stats.total > 0) ? (
          <>
            {/* Total Orders Counter at Top */}
            <div style={{
              background: theme.surface,
              borderRadius: "16px",
              padding: "24px 20px",
              marginBottom: "24px",
              border: `1px solid ${theme.border}`,
              textAlign: "center"
            }}>
              <div style={{ fontSize: "14px", color: theme.textMuted, marginBottom: "8px" }}>Total Orders</div>
              <div style={{ fontSize: "48px", fontWeight: 800, color: theme.primary, lineHeight: 1 }}>{stats.total}</div>
              <div style={{ fontSize: "13px", color: theme.textMuted, marginTop: "8px" }}>
                Total Spent: <span style={{ fontWeight: 600, color: theme.textPrimary }}>₹{stats.totalSpent.toLocaleString()}</span>
              </div>
            </div>

            {/* Filter Tabs */}
            <div style={{
              display: "flex",
              gap: "12px",
              marginBottom: "20px",
              flexWrap: "wrap",
              borderBottom: `1px solid ${theme.border}`,
              paddingBottom: "12px"
            }}>
              {[
                { key: "all", label: "All Orders", count: stats.total },
                { key: "delivered", label: "Delivered", count: stats.delivered, color: theme.success },
                { key: "pending", label: "Pending", count: stats.pending, color: theme.warning },
                { key: "cancelled", label: "Cancelled", count: stats.cancelled, color: theme.error }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => filterOrders(tab.key)}
                  style={{
                    background: activeTab === tab.key ? (tab.color || theme.primary) : "transparent",
                    color: activeTab === tab.key ? "#fff" : theme.textSecondary,
                    border: activeTab === tab.key ? "none" : `1px solid ${theme.border}`,
                    padding: "8px 20px",
                    borderRadius: "24px",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Orders Table */}
            <div style={{
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "12px",
              overflow: "hidden"
            }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                  <thead>
                    <tr style={{ background: theme.surfaceAlt }}>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Order ID</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Date & Time</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Items</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Amount</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Status</th>
                      <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => (
                      <tr
                        key={order.id || idx}
                        style={{
                          borderBottom: idx < orders.length - 1 ? `1px solid ${theme.border}` : "none",
                          transition: "background 0.2s ease"
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = theme.surfaceAlt)
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: theme.textPrimary, fontFamily: "monospace" }}>
                          #{order.id}
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: "12px", color: theme.textMuted }}>
                          {formatDateTime(order.created_at)}
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: "12px", color: theme.textSecondary }}>
                          {getItemsList(order.items)}
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: "14px", fontWeight: 700, color: theme.textPrimary }}>
                          ₹{order.final_amount || order.amount || 0}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <OrderStatusBadge status={order.order_status_user || order.order_status_restro || order.status} theme={theme} />
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <button
                            onClick={() => setSelectedOrder(order)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: theme.primary,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = `${theme.primary}1a`}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          >
                            <EyeIcon /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{
            textAlign: "center",
            padding: "80px 20px",
            background: theme.surface,
            borderRadius: "16px",
            border: `1px solid ${theme.border}`
          }}>
            <div style={{ fontSize: "80px", marginBottom: "20px" }}>🛒</div>
            <h3 style={{ margin: "0 0 12px", color: theme.textPrimary, fontSize: "20px" }}>No Orders Found</h3>
            <p style={{ margin: 0, fontSize: "14px", color: theme.textMuted }}>
              This user hasn't placed any orders yet
            </p>
          </div>
        )}
      </div>

      {/* View Order Modal */}
      {selectedOrder && (
        <ViewOrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
};

export default OrderHistory;